import type Alpine from 'alpinejs';
import type {motion} from '../motion';
import {
    del as requestDelete,
    get as requestGet,
    post as requestPost,
    put as requestPut,
    type NavigationHistory,
    type RequestOptions,
    type RequestResult,
} from './engine/request';
import {navigateWithAlpine} from './engine/navigation';
import type {sortable, UiSortableService} from './drag-and-drop/sortable';
import {warn} from '../utils';

type UiState = {
    activeRequests: number;
};

type UiRequestOptions = RequestOptions & {
    navigate?: boolean;
};
type UiGetOptions = UiRequestOptions & {
    history?: NavigationHistory;
};
type UiGet = (url: string | URL, options?: UiGetOptions) => Promise<RequestResult | void>;
type UiMutation = (url: string | URL, options?: UiRequestOptions) => Promise<RequestResult>;

type UiRuntime = {
    readonly delete: UiMutation;
    readonly get: UiGet;
    readonly post: UiMutation;
    readonly put: UiMutation;
    readonly isFetching: boolean;
    readonly motion: typeof motion;
    readonly sortable: UiSortableService;
};
type UiCapabilities = {
    motion?: typeof motion;
    sortable?: typeof sortable;
};

let state: UiState = {activeRequests: 0};
let reactive = false;
let listening = false;
let capabilities: UiCapabilities = {};
let AlpineRuntime: Alpine.Alpine | null = null;

const get: UiGet = (url, options = {}) => {
    const {history, navigate = false, ...requestOptions} = options;
    if (!navigate) return requestGet(url, requestOptions);
    if (!AlpineRuntime) throw new Error('Trellis UI runtime has not been registered with Alpine.');
    const {source, ...navigationOptions} = requestOptions;

    return navigateWithAlpine(AlpineRuntime, url, source, {
        ...navigationOptions,
        ...(history ? {history} : {}),
    });
};

const mutationMethod = (
    name: 'delete' | 'post' | 'put',
    request: (url: string | URL, options?: RequestOptions) => Promise<RequestResult>,
): UiMutation => (url, options = {}) => {
    const {navigate = false, ...requestOptions} = options;
    if (navigate) warn(`$ui.${name}: navigate is only supported for GET requests`);
    return request(url, requestOptions);
};

const post = mutationMethod('post', requestPost);
const put = mutationMethod('put', requestPut);
const del = mutationMethod('delete', requestDelete);

const ui: UiRuntime = {
    get,
    post,
    put,
    delete: del,
    get motion(): typeof motion {
        if (!capabilities.motion) throw new Error('Trellis Motion capability is not registered.');
        return capabilities.motion;
    },
    get sortable(): UiSortableService {
        if (!capabilities.sortable) throw new Error('Trellis Sortable capability is not registered.');
        return capabilities.sortable;
    },
    get isFetching(): boolean {
        return state.activeRequests > 0;
    },
};

const registerRequestState = (): void => {
    if (listening) return;
    listening = true;

    document.addEventListener('ui:request:start', () => {
        state.activeRequests += 1;
    });
    document.addEventListener('ui:request:finish', () => {
        state.activeRequests = Math.max(0, state.activeRequests - 1);
    });
};

const registerUiMagic = (AlpineInstance: Alpine.Alpine): void => {
    AlpineRuntime = AlpineInstance;
    if (!reactive) {
        state = AlpineInstance.reactive(state);
        reactive = true;
    }
    registerRequestState();
    AlpineInstance.magic('ui', () => ui);
};
const registerUiMotion = (value: typeof motion): void => {
    capabilities.motion = value;
};
const registerUiSortable = (value: typeof sortable): void => {
    capabilities.sortable = value;
};

export {registerUiMagic, registerUiMotion, registerUiSortable, ui};
export type {UiCapabilities, UiGetOptions, UiRequestOptions, UiRuntime};
